import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Modal, Row, Col, Divider, Select, Input, Form, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import TableComponent from 'components/table-component';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { API_STATUS } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { AiOutlineSearch } from 'react-icons/ai';
import { putApi } from 'redux/sagas/putApiSaga';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import './wash-type-modal.scss';

const layer1FormCol = {
	labelCol: {
		span: 24,
	},
	wrapperCol: {
		span: 24,
	},
};

const WashTypeModal = ({ value = null, onChange }) => {
	const [WashTypeModal, setWashTypeModal] = useState(false);
	const [searchKey, setSearchKey] = useState('');
	const [addFormVisible, setAddFormVisible] = useState(false);
	const [editData, setEditData] = useState(null);
	const [tableData, setTableData] = useState([]);
	const selectRef = useRef(null);
	const globalRedux = useSelector((state) => state.globalRedux);
	const washTypes = useSelector((state) => state.globalRedux?.washTypes || {});
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const column = [
		{
			title: 'Wash Type',
			dataIndex: 'categoryName',
			key: 'categoryName',
			width: '20%',
			align: 'left',
		},
		{
			title: 'Delete',
			dataIndex: 'categoryName',
			key: 'categoryName',
			width: '10%',
			align: 'center',
			render: (value, record) => (
				<Row className="show-on-hover" justify="center">
					<Col span={6} onClick={() => handleEdit(record)}>
						<EditOutlined />
					</Col>
					<Col span={6} className="delete" onClick={() => handleRemove(record?._id)}>
						<DeleteOutlined />
					</Col>
				</Row>
			),
		},
	];

	const handleInputKeyDown = () => {
		if (selectRef.current) {
			selectRef.current.blur();
			setWashTypeModal(true);
		}
	};

	const handleRemove = (id) => {
		let url = `${SERVER_IP}category/${id}?orgId=${globalRedux?.selectedOrganization?._id}`;
		dispatch(deleteApi('DELETE_WASH_TYPE', url));
	};

	const handleSubmit = (values) => {
		let data = {
			...values,
			orgId: globalRedux?.selectedOrganization?._id,
		};
		if (editData) {
			let url = `${SERVER_IP}category/${editData._id}?orgId=${globalRedux?.selectedOrganization?._id}`;
			dispatch(putApi(data, 'EDIT_WASH_TYPE', url));
		} else {
			dispatch(postApi(data, 'ADD_WASH_TYPE'));
		}
	};

	const getItemRates = useCallback(() => {
		let url = `${SERVER_IP}rate/?orgId=${globalRedux?.selectedOrganization?._id}`;
		dispatch(getApi('GET_ITEM_RATES', url));
	}, [dispatch, globalRedux?.selectedOrganization?._id]);

	const getwashTypes = useCallback(() => {
		let url = `${SERVER_IP}category?orgId=${globalRedux?.selectedOrganization?._id}`;
		dispatch(getApi('GET_WASH_TYPES', url));
	}, [dispatch, globalRedux?.selectedOrganization?._id]);

	useEffect(() => {
		if (WashTypeModal) {
			getItemRates();
			getwashTypes();
		}
	}, [getItemRates, getwashTypes, WashTypeModal]);

	const handleEdit = (values) => {
		let data = {
			...values,
			orgId: globalRedux?.selectedOrganization?._id,
			categoryName: values?.categoryName || '',
		};
		setEditData(data);
	};

	useEffect(() => {
		if ((washTypes || [])?.length > 0) {
			setTableData(washTypes);
		}
	}, [washTypes]);

	useEffect(() => {
		if (editData) {
			form.setFieldsValue({
				categoryName: editData?.categoryName || '',
			});
			setAddFormVisible(true);
		}
	}, [editData, form]);

	useEffect(() => {
		if (!addFormVisible) {
			form.resetFields();
			setEditData(null);
		}
	}, [addFormVisible, form]);

	useEffect(() => {
		if (globalRedux.apiStatus.ADD_WASH_TYPE === 'SUCCESS' || globalRedux.apiStatus.EDIT_WASH_TYPE === 'SUCCESS') {
			dispatch(resetApiStatus(editData ? 'EDIT_WASH_TYPE' : 'ADD_WASH_TYPE'));
			setAddFormVisible(false);
			getwashTypes();
		}
	}, [globalRedux.apiStatus, editData, setWashTypeModal, dispatch, getwashTypes]);

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_WASH_TYPE === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_WASH_TYPE'));
			doIt = true;
		}
		if (doIt) {
			getwashTypes();
		}
	}, [globalRedux.apiStatus, dispatch, getwashTypes]);

	const loading = globalRedux.apiStatus.GET_WASH_TYPES === API_STATUS.PENDING;
	const buttonLoading = globalRedux.apiStatus.ADD_WASH_TYPE === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_WASH_TYPE === API_STATUS.PENDING;

	const filteredData = useMemo(() => {
		if (searchKey === '') {
			return tableData;
		}
		return tableData.filter((unit) => {
			return (
				(unit?.categoryName || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(unit?.outstandingBalance || '')?.toString().toLowerCase().includes(searchKey.toLowerCase())
			);
		});
	}, [tableData, searchKey]);

	return (
		<>
			<Select
				onChange={onChange}
				ref={selectRef}
				{...{
					...(value && { value }),
				}}
				placeholder="Select wash type"
				dropdownRender={(menu) => (
					<div>
						<div style={{ display: 'flex', flexWrap: 'nowrap', cursor: 'pointer', background: '#6ebaff' }} onClick={handleInputKeyDown}>
							<a href style={{ flex: 'none', color: '#fff', padding: '8px', display: 'block' }}>
								Add wash type
							</a>
						</div>
						<Divider />
						{menu}
					</div>
				)}>
				{washTypes.map((category) => (
					<Select.Option key={category?._id} value={category?._id}>
						{category?.categoryName}
					</Select.Option>
				))}
			</Select>

			<Modal
				title={
					<Row>
						<Col span={24}>
							<Row>
								<Col span={24}>Wash Types</Col>
							</Row>
						</Col>
					</Row>
				}
				footer={null}
				onCancel={() => setWashTypeModal(false)}
				cancelButtonProps={{ onPress: () => setWashTypeModal(false) }}
				okText="Save"
				open={WashTypeModal}
				style={{ top: 0 }}
				width={'40%'}>
				<Row className="add-unit">
					<Col span={24}>
						{addFormVisible && (
							<Row className="add-unit-form">
								<Col span={24}>
									<Form
										name="add-staff"
										style={{}}
										requiredMark={false}
										colon={false}
										labelAlign="left"
										form={form}
										onFinish={handleSubmit}
										{...layer1FormCol}>
										<Row gutter={[10, 0]}>
											<Col span={15}>
												<Form.Item
													label="Wash Type"
													name="categoryName"
													initialValue={editData?.categoryName}
													rules={[
														{
															required: true,
															message: 'This Field is Required!',
														},
													]}>
													<Input autoFocus placeholder="Wash Type" />
												</Form.Item>
											</Col>
											<Col span={6}></Col>
										</Row>
										<Row gutter={[10, 0]}>
											<Col span={12}>
												<Form.Item
													style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}
													wrapperCol={{
														span: 24,
													}}>
													<Row>
														<Button loading={buttonLoading} type="primary" style={{ marginRight: 5 }} htmlType="submit">
															{editData ? 'Update' : 'Save'}
														</Button>
														<Button onClick={() => setAddFormVisible(false)} style={{}} danger>
															Cancel
														</Button>
													</Row>
												</Form.Item>
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						)}
						<TableComponent
							className="add-table"
							style={{ width: '100%' }}
							rowKey={(record) => record._id}
							dataSource={filteredData}
							columns={column}
							pagination={false}
							loading={loading}
							title={() =>
								addFormVisible ? null : (
									<Row gutter={[10, 0]} style={{ justifyContent: 'space-between' }}>
										<Col span={15}>
											<Input
												placeholder="Search Unit"
												suffix={<AiOutlineSearch />}
												onChange={({ target: { value } }) => setSearchKey(value)}
											/>
										</Col>
										<Col span={9} style={{ textAlign: 'right' }}>
											<Button type="primary" onClick={() => setAddFormVisible(true)}>
												Create New
											</Button>
										</Col>
									</Row>
								)
							}
						/>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default WashTypeModal;
